import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ProjectData } from 'types/Gantt';
import { COLOURS } from 'utils/color';
import { addMonths, formatDateLong, startOfMonth } from 'utils/date';

interface GanttChartProps {
  data: ProjectData;
  onReset: () => void;
}

const ROW_HEIGHT = 28;
const PIXELS_PER_DAY = 5; // Minimum pixels per calendar day — keeps the timeline readable
const TABLE_COLS = [
  { key: 'num', label: 'ID', width: 40 },
  { key: 'outline', label: 'WBS', width: 50 },
  { key: 'name', label: 'Task Name', width: 320 },
  { key: 'duration', label: 'Duration', width: 70 },
  { key: 'startStr', label: 'Start', width: 80 },
  { key: 'finishStr', label: 'Finish', width: 80 },
];
const TABLE_WIDTH = TABLE_COLS.reduce((s, c) => s + c.width, 0);

const PrintStyles = () => (
  <style>{`
    @media print {
      @page { size: A3 landscape; margin: 2mm; }

      html, body, #root {
        height: auto !important;
        min-height: auto !important;
        overflow: visible !important;
        margin: 0 !important;
        padding: 0 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      .min-h-screen {
        min-height: auto !important;
        height: auto !important;
        overflow: visible !important;
        background: transparent !important;
        padding: 0 !important;
      }

      .no-print { display: none !important; }

      .print-area {
        margin: 0 !important;
        padding: 0 !important;
        max-width: none !important;
        box-shadow: none !important;
        border: none !important;
        border-radius: 0 !important;
        width: max-content !important;
        min-width: 100% !important;
      }

      .gantt-scroll-area {
        overflow: visible !important;
        display: flex !important;
        width: max-content !important;
        min-width: 100% !important;
      }
    }

    @media screen {
      .print-area { max-width: 1400px; margin: 16px auto; box-shadow: 0 1px 4px rgba(0,0,0,0.12); border-radius: 4px; overflow: hidden; border-top: 1px solid ${COLOURS.border}; }
      .gantt-scroll-area { overflow-x: auto; overflow-y: hidden; }
    }
  `}</style>
);

const GanttChartView: React.FC<GanttChartProps> = ({ data, onReset }) => {
  const ganttAreaRef = useRef<HTMLDivElement>(null);
  const [ganttWidth, setGanttWidth] = useState(800);

  const { chartStart, totalDays, months } = useMemo(() => {
    const allStarts = data.tasks.map((t) => t.start.getTime());
    const allFinishes = data.tasks.map((t) => t.finish.getTime());
    const minDate = new Date(Math.min(...allStarts));
    const maxDate = new Date(Math.max(...allFinishes));

    const cStart = startOfMonth(minDate);
    const cEnd = addMonths(new Date(maxDate.getFullYear(), maxDate.getMonth(), 1), 1);
    const tDays = (cEnd.getTime() - cStart.getTime()) / 86400000;

    const mos = [];
    let cur = new Date(cStart);
    while (cur < cEnd) {
      const next = addMonths(cur, 1);
      mos.push({
        start: new Date(cur),
        end: new Date(Math.min(next.getTime(), cEnd.getTime())),
        label: cur.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
      });
      cur = next;
    }

    return { chartStart: cStart, totalDays: tDays, months: mos };
  }, [data.tasks]);

  // Minimum pixel width so the timeline never compresses below a readable size
  const ganttMinWidth = Math.max(600, totalDays * PIXELS_PER_DAY);

  useEffect(() => {
    if (!ganttAreaRef.current) return;
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) setGanttWidth(e.contentRect.width);
    });
    obs.observe(ganttAreaRef.current);
    return () => obs.disconnect();
  }, []);

  const taskIndex = useMemo(() => {
    const map: Record<number, number> = {};
    data.tasks.forEach((t, i) => {
      map[t.num] = i;
    });
    return map;
  }, [data.tasks]);

  const dateToPct = (d: Date) => {
    const days = (d.getTime() - chartStart.getTime()) / 86400000;
    return (days / totalDays) * 100;
  };

  const dateToPx = (d: Date) => {
    return (dateToPct(d) / 100) * ganttWidth;
  };

  const getWeekMarks = (monthStart: Date, monthEnd: Date) => {
    const marks = [];
    let d = new Date(monthStart);
    while (d.getDay() !== 1 && d < monthEnd) {
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    }
    while (d < monthEnd) {
      marks.push({ date: new Date(d), label: String(d.getDate()).padStart(2, '0') });
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7);
    }
    return marks;
  };

  const arrows = useMemo(() => {
    const result: Array<{ fromIdx: number; toIdx: number; fromFinish: Date; toStart: Date }> = [];
    data.tasks.forEach((task, i) => {
      task.depRefs.forEach((depNum) => {
        if (taskIndex[depNum] === undefined) return;
        const depIdx = taskIndex[depNum];
        const depTask = data.tasks[depIdx];
        result.push({ fromIdx: depIdx, toIdx: i, fromFinish: depTask.finish, toStart: task.start });
      });
    });
    return result;
  }, [data.tasks, taskIndex]);

  const todayPct = dateToPct(new Date());
  const showToday = todayPct >= 0 && todayPct <= 100;
  const chartHeight = data.tasks.length * ROW_HEIGHT;

  // ── Data-driven print scale ─────────────────────────────────────────
  // Calculate entirely from the data so we never depend on DOM measurements
  const HEADER_H = 42; // project title bar
  const COL_HEADER_H = 52; // month + week header rows
  const LEGEND_FOOTER_H = 60; // legend + footer
  const totalContentHeight = HEADER_H + COL_HEADER_H + chartHeight + LEGEND_FOOTER_H;
  const totalContentWidth = TABLE_WIDTH + ganttMinWidth;
  // A3 landscape usable area with 2 mm margins  (at 96 dpi)
  const PAGE_W = 1574; // (420 − 4) mm
  const PAGE_H = 1107; // (297 − 4) mm
  const scaleX = totalContentWidth > PAGE_W ? PAGE_W / totalContentWidth : 1;
  const scaleY = totalContentHeight > PAGE_H ? PAGE_H / totalContentHeight : 1;
  const printScale = Math.min(scaleX, scaleY);
  const needsScale = printScale < 1;

  const handlePrint = () => {
    // Measure the fully expanded gantt area so arrow pixel-positions are correct
    if (ganttAreaRef.current) {
      setGanttWidth(ganttAreaRef.current.scrollWidth);
    }
    // Let React flush the new ganttWidth to the DOM before printing
    setTimeout(() => {
      document.title = data.projectName;
      window.print();
    }, 150);
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: '#F0F2F5', fontFamily: "'Segoe UI', Helvetica, Arial, sans-serif", width: '100%' }}>
      <PrintStyles />
      {/* Dynamic print zoom – only injected when content exceeds one A3 page */}
      {needsScale && (
        <style>{`
          @media print {
            @page { margin: 0 !important; }
            .min-h-screen {
              zoom: ${printScale.toFixed(4)} !important;
              width: calc(100vw / ${printScale.toFixed(4)}) !important;
            }
          }
        `}</style>
      )}
      {/* TOOLBAR */}
      <div
        className="no-print"
        style={{
          background: COLOURS.headerBg,
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <span style={{ color: '#FFF', fontSize: 14, fontWeight: 600 }}>Timeline Gantt Chart</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onReset}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.4)',
              color: '#FFF',
              padding: '6px 16px',
              borderRadius: 4,
              fontSize: 13,
              cursor: 'pointer',
            }}>
            Upload New File
          </button>
          <button
            onClick={handlePrint}
            style={{
              background: '#9D449D',
              border: 'none',
              color: '#FFF',
              padding: '6px 20px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
            Export PDF
          </button>
        </div>
      </div>

      {/* PRINT AREA */}
      <div
        className="print-area"
        style={{
          background: '#FFF',
          margin: '0 auto',
          minWidth: '100%',
          borderRadius: 0,
          borderTop: `1px solid ${COLOURS.border}`,
        }}>
        {/* Header */}
        <div style={{ background: COLOURS.headerBg, padding: '10px 16px 8px', color: COLOURS.headerText }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{data.projectName}</div>
          <div style={{ fontSize: 9, opacity: 0.85 }}>
            Plan Owner: {data.planOwner} &nbsp;|&nbsp; Start: {formatDateLong(data.projectStart)} &nbsp;|&nbsp; Finish:{' '}
            {formatDateLong(data.projectFinish)} &nbsp;|&nbsp; Duration: {data.durationStr}
          </div>
        </div>

        {/* Content Body - Applying the gantt-scroll-area class to prevent truncation */}
        <div
          className="gantt-scroll-area"
          style={{
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            borderTop: 'none',
          }}>
          {/* LEFT: Task Table */}
          <div style={{ flexShrink: 0, width: TABLE_WIDTH, borderRight: `1px solid ${COLOURS.border}` }}>
            <div
              style={{
                display: 'flex',
                background: COLOURS.headerBgLight,
                height: 52,
                alignItems: 'center',
                borderBottom: `1px solid ${COLOURS.border}`,
              }}>
              {TABLE_COLS.map((col) => (
                <div
                  key={col.key}
                  style={{
                    width: col.width,
                    paddingLeft: 6,
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: COLOURS.headerText,
                    flexShrink: 0,
                  }}>
                  {col.label}
                </div>
              ))}
            </div>

            {data.tasks.map((task, i) => {
              const bg = i % 2 === 0 ? '#FFF' : COLOURS.rowAlt;
              const isBold = task.isSummary;
              const indent = task.indent * 16;
              return (
                <div
                  key={task.num}
                  style={{
                    display: 'flex',
                    height: ROW_HEIGHT,
                    alignItems: 'center',
                    background: bg,
                    borderBottom: `1px solid ${COLOURS.gridLine}`,
                  }}>
                  {TABLE_COLS.map((col) => {
                    const content = task[col.key as keyof typeof task] as React.ReactNode;
                    let pl = 6;
                    const fw = isBold && (col.key === 'name' || col.key === 'outline' || col.key === 'num') ? 700 : 400;
                    const fc = isBold
                      ? COLOURS.summaryText
                      : col.key === 'name' || col.key === 'num' || col.key === 'outline'
                      ? COLOURS.text
                      : COLOURS.textLight;

                    if (col.key === 'name') {
                      pl = 6 + indent;
                    }

                    return (
                      <div
                        key={col.key}
                        style={{
                          width: col.width,
                          paddingLeft: pl,
                          fontSize: 9.5,
                          fontWeight: fw,
                          color: fc,
                          flexShrink: 0,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}>
                        {content}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* RIGHT: Gantt Timeline */}
          <div ref={ganttAreaRef} style={{ flex: 1, minWidth: ganttMinWidth, position: 'relative' }}>
            <div
              style={{
                height: 52,
                background: COLOURS.headerBgLight,
                borderBottom: `1px solid ${COLOURS.border}`,
                position: 'relative',
              }}>
              {/* Month row */}
              {months.map((m, mi) => {
                const left = dateToPct(m.start) + '%';
                const w = dateToPct(m.end) - dateToPct(m.start) + '%';
                return (
                  <div
                    key={mi}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left,
                      width: w,
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRight: '1px solid rgba(74,111,165,0.5)',
                      borderBottom: '1px solid rgba(74,111,165,0.5)',
                      color: COLOURS.headerText,
                      fontSize: 9.5,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}>
                    {m.label}
                  </div>
                );
              })}

              {/* Week row */}
              {months.map((m, mi) => {
                const weeks = getWeekMarks(m.start, m.end);
                return weeks.map((w, wi) => {
                  const wEnd = new Date(
                    Math.min(
                      new Date(w.date.getFullYear(), w.date.getMonth(), w.date.getDate() + 7).getTime(),
                      m.end.getTime(),
                    ),
                  );
                  const left = dateToPct(w.date) + '%';
                  const width = dateToPct(wEnd) - dateToPct(w.date) + '%';
                  return (
                    <div
                      key={`${mi}-${wi}`}
                      style={{
                        position: 'absolute',
                        top: 26,
                        left,
                        width,
                        height: 26,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: '1px solid rgba(74,111,165,0.3)',
                        color: COLOURS.headerText,
                        fontSize: 8,
                        opacity: 0.85,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}>
                      {w.label}
                    </div>
                  );
                });
              })}
            </div>

            <div style={{ position: 'relative', height: chartHeight }}>
              {data.tasks.map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    top: i * ROW_HEIGHT,
                    left: 0,
                    right: 0,
                    height: ROW_HEIGHT,
                    background: i % 2 === 0 ? '#FFF' : COLOURS.rowAlt,
                    borderBottom: `1px solid ${COLOURS.gridLine}`,
                  }}
                />
              ))}

              {months.map((m, mi) => (
                <div
                  key={mi}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: dateToPct(m.start) + '%',
                    width: 1,
                    background: COLOURS.gridLineDark,
                    zIndex: 1,
                  }}
                />
              ))}

              {showToday && (
                <div
                  style={{
                    position: 'absolute',
                    top: -52,
                    bottom: 0,
                    left: dateToPct(new Date()) + '%',
                    width: 0,
                    borderLeft: `1.5px dashed ${COLOURS.today}`,
                    zIndex: 5,
                  }}>
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: -16,
                      fontSize: 7,
                      fontWeight: 700,
                      color: COLOURS.today,
                      width: 32,
                      textAlign: 'center',
                    }}>
                    TODAY
                  </div>
                </div>
              )}

              {data.tasks.map((task, i) => {
                const left = dateToPct(task.start);
                const right = dateToPct(task.finish);
                const barWidth = Math.max(right - left, 0.3);
                const topPos = i * ROW_HEIGHT;

                if (task.isSummary) {
                  const barH = 6;
                  const barTop = topPos + (ROW_HEIGHT - barH) / 2;
                  return (
                    <div
                      key={task.num}
                      style={{
                        position: 'absolute',
                        top: barTop,
                        left: left + '%',
                        width: barWidth + '%',
                        height: barH,
                        zIndex: 2,
                      }}>
                      <div style={{ width: '100%', height: '100%', background: task.colour, position: 'relative' }}>
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            bottom: -4,
                            width: 0,
                            height: 0,
                            borderLeft: '5px solid transparent',
                            borderRight: '5px solid transparent',
                            borderTop: `5px solid ${task.colour}`,
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            right: 0,
                            bottom: -4,
                            width: 0,
                            height: 0,
                            borderLeft: '5px solid transparent',
                            borderRight: '5px solid transparent',
                            borderTop: `5px solid ${task.colour}`,
                          }}
                        />
                      </div>
                    </div>
                  );
                }

                const barH = 14;
                const barTop = topPos + (ROW_HEIGHT - barH) / 2;
                return (
                  <div
                    key={task.num}
                    style={{
                      position: 'absolute',
                      top: barTop,
                      left: left + '%',
                      width: barWidth + '%',
                      height: barH,
                      borderRadius: 3,
                      background: task.colour,
                      zIndex: 2,
                      minWidth: 4,
                    }}
                  />
                );
              })}

              <svg
                viewBox={`0 0 ${ganttWidth} ${chartHeight}`}
                preserveAspectRatio="none"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: chartHeight,
                  pointerEvents: 'none',
                  zIndex: 3,
                }}>
                {arrows.map((arr, ai) => {
                  const fromX = dateToPx(arr.fromFinish);
                  const fromY = arr.fromIdx * ROW_HEIGHT + ROW_HEIGHT / 2;
                  const toX = dateToPx(arr.toStart);
                  const toY = arr.toIdx * ROW_HEIGHT + ROW_HEIGHT / 2;
                  const jogX = fromX + 6;
                  let linePoints = `${fromX},${fromY} ${jogX},${fromY} ${jogX},${toY} ${toX},${toY}`;
                  if (Math.abs(toX - fromX) < 6) {
                    const midY = fromY + (toY - fromY) / 2;
                    linePoints = `${fromX},${fromY} ${jogX},${fromY} ${jogX},${midY} ${fromX - 5},${midY} ${
                      fromX - 5
                    },${toY} ${toX - 2},${toY}`;
                  }
                  return (
                    <g key={ai}>
                      <polyline points={linePoints} fill="none" stroke={COLOURS.depArrow} strokeWidth={0.8} />

                      <polygon
                        points={`${toX},${toY} ${toX - 5},${toY - 3} ${toX - 5},${toY + 3}`}
                        fill={COLOURS.depArrow}
                      />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>

        {/* Dynamic Legend */}
        <div
          style={{
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            borderTop: `1px solid ${COLOURS.gridLine}`,
          }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: COLOURS.text, marginRight: 4 }}>Legend:</span>
          {data.legend.map((item) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 8 }}>
              <div style={{ width: 14, height: 8, borderRadius: 2, background: item.colour, flexShrink: 0 }} />
              <span style={{ fontSize: 8.5, color: COLOURS.text }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '4px 16px 8px',
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: `1px solid ${COLOURS.gridLine}`,
          }}>
          <span style={{ fontSize: 8, color: COLOURS.textMuted }}>
            Generated: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}{' '}
            &nbsp;|&nbsp; Element Engineering Australia &nbsp;|&nbsp; Confidential
          </span>
          <span style={{ fontSize: 8, color: COLOURS.textMuted }}>{data.projectName?.split(' ')[0]}</span>
        </div>
      </div>
    </div>
  );
};

export default GanttChartView;
